import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Card, CardHeader, Button, CardBody, Row, Col, Alert } from "reactstrap";
import { useContext } from "react";
import KybCompanyContext from "../../../../contexts/KYBCompanyContext";
import { useQueries } from "@tanstack/react-query";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import { KYBAvailableServices } from "src/modules/3rd-party-management/constants";
import { RiCloseCircleLine, RiInformationLine } from "react-icons/ri";

const Confirmation = ({
    t,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        supplierData,
        companyData,
        handleFetchReports
    } = useContext(KybCompanyContext);

    const {
        services: selectedServices,
        reports
    } = companyData;

    const selectedServicesWithReports = selectedServices.filter((selectedService) => {
        return parseInt(reports[selectedService]?.length || 0) > 0
    })

    const queries = useQueries({
        queries: selectedServicesWithReports.map((service) => {
            return {
                queryKey: ['3rd-party-management-fetch-organization-credits', supplierData.id, service],
                queryFn: async () => {
                    const kybServiceObj = LicenseManagementService.getInstance();

                    return await kybServiceObj.fetchkYBLicenseDetail({
                        provider: service,
                        detail: false
                    });
                },
                refetchOnWindowFocus: false
            }
        })
    });

    const queriesResults = selectedServicesWithReports.map((service, index) => ({
        id: service,
        ...queries[index]
    }));

    let canClickNext = false;

    let allQueriesFetched = queriesResults.filter((q) => !q.isFetching).length === queriesResults.length;

    let numberOfReportsCanTake = queriesResults.filter((fetchQuery) => {
        if (fetchQuery.isSuccess) {
            const serviceAvailableTotalCredit = parseInt(fetchQuery.data?.totalCredits || 0);
            const neededCredit = parseInt(reports[fetchQuery.id]?.length || 0);

            return (serviceAvailableTotalCredit - neededCredit) >= 0
        }

        return false;
    }).length;

    if (allQueriesFetched && numberOfReportsCanTake > 0) {
        canClickNext = true;
    }

    return (
        <Row>
            <Col sm="12">
                <Alert color="info" className="d-flex align-items-center justify-content-start gap-2">
                    <RiInformationLine size={40} />
                    <div>
                        <strong>{t('{{creditsLabel}} Required', {
                            creditsLabel: 'Credits'
                        })}</strong>
                        <p className="mb-0">
                            {t('Review the number of {{creditsLabel}} that will be used to retrieve the selected reports. Each provider and report may consume a different number of {{creditsLabel}}. Make sure you’re aware of the total usage before proceeding. No {{creditsLabel}} will be deducted until you confirm.', {
                                creditsLabel: 'credits'
                            })}
                        </p>
                    </div>
                </Alert>
            </Col>

            {KYBAvailableServices.filter(service => service.enableFetchReport && selectedServicesWithReports.includes(service.id)).map((service, i) => {
                const queryResult = queriesResults.find((q) => q.id === service.id);
                const reportsCount = reports[service.id]?.length || 0;

                const totalCredits = parseInt(queryResult.data?.totalCredits || 0);

                const hasEnoughCredit = queryResult.isFetching ? true : (totalCredits - reportsCount) >= 0;

                return (
                    <Col sm="12" key={i}>
                        <Card
                            className={`border ${!hasEnoughCredit ? 'border-danger' : ''}`}
                        >
                            <CardHeader className={hasEnoughCredit ? 'bg-white' : 'bg-danger'}>
                                <h5 
                                    className={`mb-1 ${hasEnoughCredit ? 'text-dark' : 'text-white'}`}
                                >
                                    {service.title} – {t('{{creditLabel}} Usage', {
                                        creditLabel: "Credit"
                                    })}
                                </h5>
                            </CardHeader>

                            <CardBody>
                                <Row>
                                    <Col sm="12" md="4" lg="3" className="mb-3">
                                        <strong>
                                            {t('Your balance')}:
                                        </strong>
                                    </Col>
                                    <Col sm="12" md="8" lg="9" className="mb-3">
                                        {queryResult.isFetching ? (
                                            <div
                                                className="dt-field dt-skeleton"
                                                style={{ width: '70%', height: '15px' }}
                                            />
                                        ) : (
                                            hasEnoughCredit ? totalCredits : (
                                                <span className="text-danger">
                                                    <RiCloseCircleLine className="me-1" size={24} />
                                                    {t('Not Enough {{creditLabel}}', {
                                                        creditLabel: 'Credit'
                                                    })}
                                                </span>
                                            )
                                        )}
                                    </Col>

                                    <Col sm="12" md="4" lg="3" className="mb-3">
                                        <strong>
                                            {t('Selected Reports')}:
                                        </strong>
                                    </Col>
                                    <Col sm="12" md="8" lg="9" className="mb-3">
                                        {queryResult.isFetching ? (
                                            <div
                                                className="dt-field dt-skeleton"
                                                style={{ width: '70%', height: '15px' }}
                                            />
                                        ) : (
                                            reportsCount
                                        )}
                                    </Col>

                                    <Col sm="12" md="4" lg="3" className="mb-3">
                                        <strong>
                                            {t('Total {{creditsLabel}}', {
                                                creditsLabel: 'credit(s)'
                                            })}:
                                        </strong>
                                    </Col>
                                    <Col sm="12" md="8" lg="9" className="mb-3">
                                        {queryResult.isFetching ? (
                                            <div
                                                className="dt-field dt-skeleton"
                                                style={{ width: '70%', height: '15px' }}
                                            />
                                        ) : (
                                            `${reportsCount} Credits`
                                        )}
                                    </Col>

                                    <Col sm="12" md="4" lg="3" className="mb-3">
                                        <strong>
                                            {t('Balance after purchase')}:
                                        </strong>
                                    </Col>
                                    <Col sm="12" md="8" lg="9" className="mb-3">
                                        {queryResult.isFetching ? (
                                            <div
                                                className="dt-field dt-skeleton"
                                                style={{ width: '70%', height: '15px' }}
                                            />
                                        ) : (
                                            `${totalCredits - reportsCount} Credits`
                                        )}
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                )
            })}

            <Col sm="12">
                {/* Submit Button */}
                <div className="d-flex justify-content-between">
                    <Button
                        color="secondary"
                        type="button"
                        outline
                        onClick={onCancelButtonClicked}
                    >
                        {t('Cancel')}
                    </Button>

                    <div>
                        <Button
                            color="secondary"
                            onClick={onBackButtonClicked}
                            className="me-2"
                            type="button"
                        >
                            {t('Back')}
                        </Button>

                        <Button
                            color="primary"
                            type="button"
                            onClick={handleFetchReports}
                            disabled={!canClickNext}
                        >
                            {t('Confirm & Fetch')}
                        </Button>
                    </div>
                </div>
            </Col>
        </Row>
    );
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps,
        {}
    )(Confirmation)
)